import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EditorCreateSignUpFieldEventCallback } from '@dev/template-editor';
import { ContactFieldType, CustomField, MergeField } from '@dev/ee-api/ee-api-models';
import { ShowDialogParams } from '@/store/dialog/types';
import { HideDialog, UpdateDialog } from '@/store/dialog/actions';
import { PutContactFieldStart, getCustomFieldsStore } from '@/store/customFields/actions';
import { getMergeFieldsStore } from '@/store/mergeFields/actions';
import { mapMergeFieldToEditorSignUpField } from '@/services/tools/mapMergeFieldToEditorSignUpField';
import { AddCustomFieldModal } from '../../components/AddCustomFieldModal';

interface CustomFieldOptions {
    isCustom: boolean,
    isLengthSetable: boolean,
    isNameError: boolean,
    isLengthError: boolean,
    type: string,
}

export type CreateCustomFieldModalType = CustomField & CustomFieldOptions;

const isFieldInvalid = (field: CreateCustomFieldModalType): boolean => field?.isLengthError || field?.isNameError || !field?.Name;

interface CreateFieldModalProps {
    popBackCallback: () => void;
}

export const useCreateFieldModalParams = ({ popBackCallback }: CreateFieldModalProps): {
    getCreateFieldModalProps: (callback: EditorCreateSignUpFieldEventCallback) => ShowDialogParams;
} => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { isAddingField, lastAddedFieldName } = useSelector(getCustomFieldsStore);
    const { maxFieldsValuesMap } = useSelector(getMergeFieldsStore)

    const fieldToAdd = useRef<CreateCustomFieldModalType>();
    
    const onClose = (): void => {
        popBackCallback();
    };

    useEffect((): void => {
        if (!isAddingField && !!lastAddedFieldName && lastAddedFieldName === fieldToAdd.current?.Name) {
            dispatch(HideDialog());
            onClose();
            return;
        }
        dispatch(UpdateDialog({ isDisabled: isAddingField || isFieldInvalid(fieldToAdd.current)}));
    }, [isAddingField]);

    const getCreateFieldModalProps = (callback: EditorCreateSignUpFieldEventCallback): ShowDialogParams => {
        fieldToAdd.current = null;
        const onSaveClick = (): void => {
            if (!fieldToAdd.current) {
                return;
            }
            const isInvalid = isFieldInvalid(fieldToAdd.current);
            if (isInvalid) {
                dispatch(UpdateDialog({ isDisabled: true }));
                return;
            }
            const successCallback = (mergeField: MergeField): void => {
                if (!mergeField) {
                    return;
                }
                callback?.(mapMergeFieldToEditorSignUpField(mergeField, maxFieldsValuesMap));
            };
            dispatch(PutContactFieldStart(
                { 
                    Name: fieldToAdd.current.Name, 
                    Type: fieldToAdd.current.FieldType as any as ContactFieldType, 
                    Length: fieldToAdd.current.Length
                }, 
                fieldToAdd.current.FieldType,
                successCallback,
            ));
        };

        const onChange = (field: CreateCustomFieldModalType): void => {
            fieldToAdd.current = field;
            dispatch(UpdateDialog({ isDisabled: isFieldInvalid(field) }));
        };

        const body = (
            <AddCustomFieldModal onChange={onChange} />
        );

        return {
            title: t('addcustomfields'),
            confirmLabel: t('addcustomfields'),
            onClickConfirm: onSaveClick,
            cancelLabel: t('cancel'),
            onClickCancel: onClose,
            isDisabled: true,
            body,
            id: 'create-field-modal',
            width: '816px',
            isHiddenManually: true,
        };
    };
    return { getCreateFieldModalProps };
};
